import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Member } from '@/models/member.model';
import { MemberService } from '@/services/member.service';
import { Document, DocumentType } from '@/models/document.model';
import { DocumentService } from '@/services/document.service';
import { PdfService } from '@/services/pdf.service';
import { ArrowLeft, Mail, Phone, Calendar, CreditCard, Shield, FileText, Download } from 'lucide-react';
import { format } from 'date-fns';

export const MemberDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [member, setMember] = useState<Member | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'documents'>('overview');

    useEffect(() => {
        if (id) {
            loadData(id);
        }
    }, [id]);

    const loadData = async (memberId: string) => {
        setLoading(true);
        try {
            const [memberData, docsData] = await Promise.all([
                MemberService.getMemberById(memberId),
                DocumentService.getDocumentsByEntity('member', memberId),
            ]);
            setMember(memberData);
            setDocuments(docsData);
        } catch (error) {
            console.error('Error loading member data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async (e: React.MouseEvent, document: Document) => {
        e.preventDefault();
        try {
            await PdfService.downloadDocumentAsPdf(document);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Failed to download PDF');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!member) {
        return (
            <div className="p-8 text-center">
                <h2 className="text-xl font-semibold text-gray-900">Member not found</h2>
                <Link to="/enrollment" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
                    Return to Dashboard
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-6">
                <Link to="/enrollment" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to Onboarding
                </Link>
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{member.firstName} {member.lastName}</h1>
                        <div className="flex items-center mt-2 space-x-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${member.isActive ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {member.isActive ? 'Active' : 'Onboarding'}
                            </span>
                            <span className="text-sm text-gray-500">Member ID: {member.id}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('documents')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'documents'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        Documents ({documents.length})
                    </button>
                </nav>
            </div>

            {activeTab === 'overview' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                        <div className="space-y-4">
                            <div className="flex items-center text-gray-600">
                                <Mail className="w-5 h-5 mr-3" />
                                <span>{member.email}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                                <Phone className="w-5 h-5 mr-3" />
                                <span>{member.phoneNumber || 'No phone number'}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                                <Calendar className="w-5 h-5 mr-3" />
                                <span>Enrolled: {format(member.enrollmentDate, 'MMM d, yyyy')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Status & Banking */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Account Status</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center">
                                    <Shield className="w-5 h-5 text-gray-400 mr-3" />
                                    <span className="text-gray-700">Account Verification</span>
                                </div>
                                {member.userId ? (
                                    <span className="text-green-600 font-medium text-sm">Created</span>
                                ) : (
                                    <span className="text-yellow-600 font-medium text-sm">Pending</span>
                                )}
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center">
                                    <CreditCard className="w-5 h-5 text-gray-400 mr-3" />
                                    <span className="text-gray-700">Bank Connection</span>
                                </div>
                                {member.bankAccountVerified ? (
                                    <div className="text-right">
                                        <span className="block text-green-600 font-medium text-sm">Verified</span>
                                        <span className="text-xs text-gray-500">{member.bankAccountBankName} •••• {member.bankAccountLast4}</span>
                                    </div>
                                ) : (
                                    <span className="text-gray-500 font-medium text-sm">Not Connected</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* Documents Tab */
                <div className="space-y-6">
                    {/* Required Documents Checklist */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Required Documents Checklist</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {['contract', 'id_card', 'insurance_card'].map(type => {
                                const isUploaded = documents.some(d => d.type === type || d.tags.includes(type));
                                return (
                                    <div key={type} className={`p-4 rounded-lg border ${isUploaded ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium capitalize">{type.replace('_', ' ')}</span>
                                            {isUploaded ? (
                                                <span className="text-green-600 text-sm font-bold">Uploaded</span>
                                            ) : (
                                                <span className="text-red-600 text-sm font-bold">Missing</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900">All Documents</h3>
                            <Link
                                to={`/documents/new?relatedEntityType=member&relatedEntityId=${member.id}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                            >
                                Upload Document
                            </Link>
                        </div>

                        {documents.length === 0 ? (
                            <div className="p-12 text-center text-gray-500">
                                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                                <p>No documents found for this member.</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-200">
                                {documents.map((doc) => (
                                    <li key={doc.id} className="p-6 hover:bg-gray-50">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <div className="mr-4">
                                                    <div className={`p-2 rounded-lg ${doc.type === 'contract' ? 'bg-blue-100 text-blue-600' :
                                                        doc.type === 'policy' ? 'bg-purple-100 text-purple-600' :
                                                            'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        <FileText className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900">{doc.name}</h4>
                                                    <p className="text-sm text-gray-500">
                                                        {doc.type.replace('_', ' ')} • {format(doc.createdAt, 'MMM d, yyyy')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${doc.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {doc.status}
                                                </span>
                                                <button
                                                    onClick={(e) => handleDownloadPdf(e, doc)}
                                                    className="text-gray-400 hover:text-blue-600"
                                                    title="Download"
                                                >
                                                    <Download className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
